import { useMemo, useState } from 'react';
import {
  ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { detectLeaksFromSeries, DEFAULT_DECLINE_PER_DAY } from '../lib/leakDetect';

const RATE_COLOR = '#2a78d6';
const EXPECTED_COLOR = '#9a9a9a';
const LEAK_COLOR = '#d64545';
const GAS_COLOR = '#e0902a';

function LeakTooltip({ active, payload, useWgr }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="custom-tooltip">
      <div className="tt-time">{new Date(p.time).toLocaleString()}</div>
      {useWgr ? (
        <>
          <div className="tt-value">water/gas ratio: <b>{p.rate.toFixed(3)}</b></div>
          {p.fill != null && <div className="tt-value">water (fill): {p.fill.toFixed(2)} in/hr</div>}
          {p.gas != null && <div className="tt-value">gas: {p.gas.toFixed(0)} mcf/day</div>}
          {p.expected != null && <div className="tt-value">expected ratio: {p.expected.toFixed(3)}</div>}
        </>
      ) : (
        <>
          <div className="tt-value">fill rate: <b>{p.rate.toFixed(2)} in/hr</b></div>
          {p.expected != null && <div className="tt-value">expected: {p.expected.toFixed(2)} in/hr</div>}
        </>
      )}
      {p.leak && <div className="tt-value" style={{ color: LEAK_COLOR, fontWeight: 600 }}>LEAK — {p.dropPct.toFixed(0)}% below expected</div>}
    </div>
  );
}

function BendTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="custom-tooltip">
      <div className="tt-time">{new Date(p.time).toLocaleString()}</div>
      <div className="tt-value">fill slowed: <b>{p.bend.toFixed(0)}%</b></div>
      {p.early != null && <div className="tt-value">early: {p.early.toFixed(2)} in/hr</div>}
      {p.late != null && <div className="tt-value">late: {p.late.toFixed(2)} in/hr</div>}
      {p.gasChange != null && (
        <div className="tt-value">gas change (same window): {p.gasChange >= 0 ? '+' : ''}{p.gasChange.toFixed(1)}%</div>
      )}
      {p.vol != null && <div className="tt-value">haul volume: {p.vol.toFixed(1)} in</div>}
      {p.leakBend != null && (
        <div className="tt-value" style={{ color: LEAK_COLOR, fontWeight: 600 }}>
          LEAK — water slowed while gas held steady
        </div>
      )}
    </div>
  );
}

/**
 * LeakDetector — per-well fill-rate-over-time view that flags leak events
 * (meaningful drops in the positive fill slope beyond natural decline).
 */
function LeakDetector({ hauls, wellName, onReviewHaul }) {
  const [windowN, setWindowN] = useState(5);
  const [dropPct, setDropPct] = useState(15);
  const [minFills, setMinFills] = useState(2);
  const [requirePersist, setRequirePersist] = useState(true);
  const [declinePctDay, setDeclinePctDay] = useState(DEFAULT_DECLINE_PER_DAY * 100);

  // Does this well have gas data? If so, offer the water/gas-ratio metric,
  // which isolates real leaks (water falling while gas holds) from natural
  // decline (water and gas fall together).
  const hasGas = useMemo(
    () => hauls.some(h => h.gas_rate_mcf_day != null && h.gas_rate_mcf_day > 1),
    [hauls]
  );
  // Default to the water/gas ratio when gas is available.
  const [metric, setMetric] = useState('wgr');
  const useWgr = metric === 'wgr' && hasGas;

  // Intra-fill slope-bend leak signal: a single fill that slows down partway
  // through (rate of fill drops while production is unchanged) means water is
  // leaking as the tank fills. Threshold = how much the fill must slow (%).
  const [bendThreshold, setBendThreshold] = useState(15);
  // Cross-check: only count a bend as a leak if GAS held constant across the
  // same fill window (water slope changed while production did not). If gas
  // moved too, the bend can be explained by a production change, not a leak.
  const [requireStableGas, setRequireStableGas] = useState(true);
  // Any haul carries a gas cross-check result?
  const bendHasGas = useMemo(
    () => hauls.some(h => h.fill_bend_pct != null && h.fill_bend_gas_change_pct != null),
    [hauls]
  );

  const { bendSeries, bendLeaks } = useMemo(() => {
    const gate = requireStableGas && bendHasGas;
    const s = hauls
      .filter(h => h.drop_start && h.fill_bend_pct != null)
      .map(h => ({
        t: new Date(h.drop_start),
        time: h.drop_start,
        bend: h.fill_bend_pct,
        early: h.fill_early_slope_ft_per_hr,
        late: h.fill_late_slope_ft_per_hr,
        vol: h.haul_volume_ft,
        gasChange: h.fill_bend_gas_change_pct,
        // Gas-confirmed leak flag from the generator (bend big + gas stable).
        gasConfirmed: h.fill_bend_is_leak === true,
      }))
      .sort((a, b) => a.t - b.t);
    // A haul is flagged when it slowed >= threshold AND (if gating) gas stayed
    // constant across that fill.
    const isLeak = p => p.bend >= bendThreshold && (!gate || p.gasConfirmed);
    return {
      bendSeries: s.map(p => ({ ...p, leakBend: isLeak(p) ? p.bend : null })),
      bendLeaks: s.filter(isLeak),
    };
  }, [hauls, bendThreshold, requireStableGas, bendHasGas]);

  const series = useMemo(
    () => hauls
      .map(h => {
        const fill = h.post_slope_measured_ft_per_hr != null
          ? h.post_slope_measured_ft_per_hr
          : h.post_slope_ft_per_hr;
        const gas = h.gas_rate_mcf_day;
        // Water/gas ratio (scaled x1000 for readability). Only the trend
        // matters for leak detection; a declining ratio = water lost vs gas.
        const wgr = (gas != null && gas > 1 && fill != null)
          ? (fill / gas) * 1000 : null;
        return { ...h, _fill: fill, _gas: gas, _wgr: wgr };
      })
      .filter(h => h.drop_start && (useWgr
        ? (h._wgr != null && h._wgr > 0)
        : ((h._fill || 0) > 0.05)))
      .map(h => ({
        t: new Date(h.drop_start), time: h.drop_start,
        rate: useWgr ? h._wgr : h._fill,
        fill: h._fill, gas: h._gas, wgr: h._wgr,
      }))
      .sort((a, b) => a.t - b.t),
    [hauls, useWgr]
  );

  const { rows, events } = useMemo(
    () => detectLeaksFromSeries(series, {
      window: windowN,
      dropThresh: dropPct / 100,
      declinePerDay: declinePctDay / 100,
      minFills,
      requirePersist,
    }),
    [series, windowN, dropPct, declinePctDay, minFills, requirePersist]
  );

  const leakRows = rows.filter(r => r.leak);
  const validRows = rows.filter(r => r.expected != null);

  if (rows.length < 8) {
    return <div className="no-data">Not enough fill-rate history for {wellName} to detect leaks.</div>;
  }

  const metricLabel = useWgr ? 'Water/gas ratio' : 'Fill rate (in/hr)';

  return (
    <div className="leak-wrap">
      <div className="err-stats-row">
        <div className="err-stat">
          <div className="err-stat-label">Leak events</div>
          <div className="err-stat-value" style={{ color: events.length ? LEAK_COLOR : 'inherit' }}>
            {events.length}
          </div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Flagged fills</div>
          <div className="err-stat-value">{leakRows.length}</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Fills analyzed</div>
          <div className="err-stat-value">{validRows.length}</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Worst drop</div>
          <div className="err-stat-value" style={{ color: LEAK_COLOR }}>
            {leakRows.length ? `${Math.max(...leakRows.map(r => r.dropPct)).toFixed(0)}%` : '\u2014'}
          </div>
        </div>
      </div>

      <div className="error-plot-card">
        <div className="voldist-head">
          <h2>{useWgr ? 'Water/gas ratio' : 'Fill-rate'} over time &amp; leak detection — {wellName}</h2>
          <div className="voldist-controls">
            {hasGas && (
              <label title="Water/gas ratio isolates leaks (water falls while gas holds) from natural decline (both fall together)">
                Metric
                <select value={metric} onChange={e => setMetric(e.target.value)}>
                  <option value="wgr">Water/gas ratio</option>
                  <option value="fill">Fill rate only</option>
                </select>
              </label>
            )}
            <label>
              Baseline window
              <select value={windowN} onChange={e => setWindowN(Number(e.target.value))}>
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={8}>8</option>
                <option value={12}>12</option>
              </select>
            </label>
            <label>
              Drop threshold
              <input type="number" value={dropPct} step={5} min={5}
                onChange={e => setDropPct(Number(e.target.value))} />
              <span>%</span>
            </label>
            <label>
              Min consecutive fills
              <select value={minFills} onChange={e => setMinFills(Number(e.target.value))}>
                <option value={1}>1 (all dips)</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </label>
            <label>
              Natural decline
              <input type="number" value={declinePctDay} step={0.05}
                onChange={e => setDeclinePctDay(Number(e.target.value))} />
              <span>%/day</span>
            </label>
            <label title="Reject dips that recover on their own — a real leak does not heal itself">
              <input type="checkbox" checked={requirePersist}
                onChange={e => setRequirePersist(e.target.checked)} />
              Must not recover
            </label>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={rows} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid stroke="#e1e0d9" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="ms"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={ms => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              tick={{ fontSize: 11, fill: '#898781' }}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11, fill: '#898781' }}
              label={{ value: metricLabel, angle: -90, position: 'insideLeft', fontSize: 12, fill: '#898781' }}
            />
            {useWgr && (
              <YAxis
                yAxisId="gas" orientation="right"
                tick={{ fontSize: 11, fill: GAS_COLOR }}
                label={{ value: 'Gas (mcf/day)', angle: 90, position: 'insideRight', fontSize: 12, fill: GAS_COLOR }}
              />
            )}
            <Tooltip content={<LeakTooltip useWgr={useWgr} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              yAxisId="left"
              name={useWgr ? 'Water/gas ratio' : 'Fill rate'} dataKey="rate" stroke={RATE_COLOR} strokeWidth={1.6}
              dot={{ r: 2 }} isAnimationActive={false} connectNulls
            />
            <Line
              yAxisId="left"
              name={useWgr ? 'Expected ratio' : 'Expected (decline-adj.)'} dataKey="expected" stroke={EXPECTED_COLOR}
              strokeWidth={1.4} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls
            />
            {useWgr && (
              <Line
                yAxisId="gas"
                name="Gas rate" dataKey="gas" stroke={GAS_COLOR} strokeWidth={1.4}
                dot={false} isAnimationActive={false} connectNulls
              />
            )}
            <Scatter yAxisId="left" name="Leak" dataKey="leakRate" fill={LEAK_COLOR} shape="circle" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="voldist-legend">
          <span><i style={{ background: RATE_COLOR }} /> {useWgr ? 'water/gas ratio' : 'measured fill rate'}</span>
          {useWgr && <span><i style={{ background: GAS_COLOR }} /> gas rate (mcf/day)</span>}
          <span><i style={{ background: EXPECTED_COLOR }} /> expected (natural decline)</span>
          <span><i style={{ background: LEAK_COLOR }} /> leak (meaningful drop)</span>
        </div>
        {useWgr && (
          <div className="voldist-legend" style={{ marginTop: 4, fontStyle: 'italic' }}>
            <span>A leak shows as the water/gas ratio dropping while gas holds steady. If both fall together, that is natural decline, not a leak.</span>
          </div>
        )}
      </div>

      <div className="error-plot-card">
        <h2 style={{ marginTop: 0 }}>Leak events ({events.length})</h2>
        {events.length === 0 ? (
          <div className="no-data" style={{ padding: '16px 0' }}>
            No leaks detected — fill rate follows the natural decline.
          </div>
        ) : (
          <table className="haul-table leak-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Start</th>
                <th>Duration</th>
                <th>Fills flagged</th>
                <th>Max drop</th>
                <th>Rate at leak</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => {
                const durDays = ((ev.lastT - ev.startT) / 86400000);
                const firstPt = ev.points[0];
                return (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{ev.startT.toLocaleDateString()}</td>
                    <td>{durDays < 1 ? 'single fill' : `${durDays.toFixed(1)} d`}</td>
                    <td>{ev.points.length}</td>
                    <td style={{ color: LEAK_COLOR, fontWeight: 600 }}>{ev.maxDrop.toFixed(0)}%</td>
                    <td>{firstPt.rate.toFixed(2)} in/hr (exp {firstPt.expected.toFixed(2)})</td>
                    <td>
                      {onReviewHaul && (
                        <button
                          className="err-review-btn"
                          title="Open this leak on the tank-level chart"
                          onClick={() => onReviewHaul({ drop_start: firstPt.time })}
                        >
                          Review on chart →
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {bendSeries.length >= 4 && (
        <div className="error-plot-card">
          <div className="voldist-head">
            <h2>Intra-fill slope bend (leak within a single fill) — {wellName}</h2>
            <div className="voldist-controls">
              <label title="How much the fill must slow down within one fill to be flagged as a possible leak">
                Slow-down threshold
                <select value={bendThreshold} onChange={e => setBendThreshold(Number(e.target.value))}>
                  <option value={10}>10%</option>
                  <option value={15}>15%</option>
                  <option value={20}>20%</option>
                  <option value={25}>25%</option>
                  <option value={30}>30%</option>
                </select>
              </label>
              {bendHasGas && (
                <label title="Only flag a bend as a leak if gas production held constant (±10%) across that fill. If gas moved too, the bend may be a production change, not a leak.">
                  <input
                    type="checkbox"
                    checked={requireStableGas}
                    onChange={e => setRequireStableGas(e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  Require stable gas
                </label>
              )}
            </div>
          </div>
          <div className="err-stats-row" style={{ marginBottom: 8 }}>
            <div className="err-stat">
              <div className="err-stat-label">Fills flagged</div>
              <div className="err-stat-value" style={{ color: bendLeaks.length ? LEAK_COLOR : 'inherit' }}>
                {bendLeaks.length}
              </div>
            </div>
            <div className="err-stat">
              <div className="err-stat-label">Fills analyzed</div>
              <div className="err-stat-value">{bendSeries.length}</div>
            </div>
            <div className="err-stat">
              <div className="err-stat-label">Worst slow-down</div>
              <div className="err-stat-value" style={{ color: LEAK_COLOR }}>
                {bendSeries.length ? `${Math.max(...bendSeries.map(p => p.bend)).toFixed(0)}%` : '\u2014'}
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={bendSeries} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2c2b28" />
              <XAxis dataKey="time" tickFormatter={t => new Date(t).toLocaleDateString()}
                tick={{ fontSize: 11, fill: '#898781' }} minTickGap={40} />
              <YAxis tick={{ fontSize: 11, fill: '#898781' }}
                label={{ value: 'fill slowed (%)', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#898781' }} />
              <Tooltip content={<BendTooltip />} />
              <ReferenceLine y={0} stroke="#4a4a46" />
              <ReferenceLine y={bendThreshold} stroke={LEAK_COLOR} strokeDasharray="4 3"
                label={{ value: `${bendThreshold}%`, fontSize: 10, fill: LEAK_COLOR, position: 'right' }} />
              <Line name="fill slow-down" dataKey="bend" stroke={RATE_COLOR} strokeWidth={1.4}
                dot={{ r: 1.6 }} isAnimationActive={false} />
              <Scatter name="Leak" dataKey="leakBend" fill={LEAK_COLOR} shape="circle" isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="voldist-legend" style={{ marginTop: 4, fontStyle: 'italic' }}>
            <span>A clean tank fills at a constant slope (near 0%). If the fill rate drops partway through one fill while gas production holds steady, the tank is losing water as it fills — a leak. {bendHasGas && requireStableGas ? 'Only bends where gas stayed within ±10% across the fill are flagged (a bend with a matching gas change is a production change, not a leak).' : ''}</span>
          </div>

          {bendLeaks.length > 0 && (
            <table className="haul-table leak-table" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fill date</th>
                  <th>Early rate</th>
                  <th>Late rate</th>
                  <th>Slowed by</th>
                  {bendHasGas && <th>Gas change</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {bendLeaks.slice().sort((a, b) => b.bend - a.bend).map((p, i) => (
                  <tr key={p.time}>
                    <td>{i + 1}</td>
                    <td>{p.t.toLocaleDateString()}</td>
                    <td>{p.early != null ? `${p.early.toFixed(2)} in/hr` : '\u2014'}</td>
                    <td>{p.late != null ? `${p.late.toFixed(2)} in/hr` : '\u2014'}</td>
                    <td style={{ color: LEAK_COLOR, fontWeight: 600 }}>{p.bend.toFixed(0)}%</td>
                    {bendHasGas && (
                      <td title="Gas production change across the same fill window">
                        {p.gasChange != null ? `${p.gasChange >= 0 ? '+' : ''}${p.gasChange.toFixed(1)}%` : '\u2014'}
                      </td>
                    )}
                    <td>
                      {onReviewHaul && (
                        <button
                          className="err-review-btn"
                          title="Open this fill on the tank-level chart"
                          onClick={() => onReviewHaul({ drop_start: p.time })}
                        >
                          Review on chart →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default LeakDetector;
